"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-system/css";
import { Search, X } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, type SelectOption } from "@/components/ui/select";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

export type CobroRow = {
  id: string;
  clientId: string | null;
  clientName: string;
  serviceId: string | null;
  description: string;
  amount: number;
  paymentMethod: string;
  status: "pagado" | "pendiente";
  date: string;
  notes: string | null;
};

type ClientOption = { id: string; label: string };
type ServiceOption = { id: string; label: string; price: number | null };

const PAYMENT_METHOD_OPTIONS: SelectOption<string>[] = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "tarjeta_debito", label: "Tarjeta de débito" },
  { value: "tarjeta_credito", label: "Tarjeta de crédito" },
  { value: "otro", label: "Otro" },
];

const label = css({ fontSize: "xs", color: "#64748b" });

function todayDateOnly(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Combobox liviano: buscar en una lista ya cargada, o tipear un valor libre si no aparece. */
function EntityPicker({
  placeholder,
  items,
  loading,
  selectedId,
  search,
  onSearchChange,
  onSelect,
  manualMode,
  onToggleManual,
  manualValue,
  onManualChange,
  manualPlaceholder,
  manualToggleLabel,
}: {
  placeholder: string;
  items: { id: string; label: string }[];
  loading: boolean;
  selectedId: string | null;
  search: string;
  onSearchChange: (v: string) => void;
  onSelect: (item: { id: string; label: string }) => void;
  manualMode: boolean;
  onToggleManual: (v: boolean) => void;
  manualValue: string;
  onManualChange: (v: string) => void;
  manualPlaceholder: string;
  manualToggleLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointer);
    return () => document.removeEventListener("mousedown", onDocPointer);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items.slice(0, 30);
    return items.filter((i) => i.label.toLowerCase().includes(q)).slice(0, 30);
  }, [items, search]);

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
      {manualMode ? (
        <Input
          value={manualValue}
          onChange={(e) => onManualChange(e.target.value)}
          placeholder={manualPlaceholder}
        />
      ) : (
        <div ref={rootRef} className={css({ position: "relative" })}>
          <div className={css({ position: "relative" })}>
            <Search
              size={14}
              aria-hidden
              className={css({ position: "absolute", left: "3", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" })}
            />
            <Input
              value={search}
              onChange={(e) => {
                onSearchChange(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className={css({ pl: "8" })}
            />
            {selectedId && (
              <button
                type="button"
                onClick={() => {
                  onSearchChange("");
                  onSelect({ id: "", label: "" });
                }}
                aria-label="Limpiar selección"
                className={css({
                  position: "absolute",
                  right: "2",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  bg: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  _hover: { color: "#475569" },
                })}
              >
                <X size={14} aria-hidden />
              </button>
            )}
          </div>
          {open && !loading && (
            <ul
              role="listbox"
              className={css({
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                zIndex: 50,
                bg: "white",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                rounded: "lg",
                shadow: "md",
                overflow: "hidden",
                maxH: "48",
                overflowY: "auto",
                py: "1",
              })}
            >
              {filtered.length === 0 ? (
                <li className={css({ px: "3", py: "2", fontSize: "sm", color: "#94a3b8" })}>Sin resultados</li>
              ) : (
                filtered.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelect(item);
                        setOpen(false);
                      }}
                      className={css({
                        display: "block",
                        w: "100%",
                        px: "3",
                        py: "2",
                        fontSize: "sm",
                        textAlign: "left",
                        bg: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#0f172a",
                        _hover: { bg: "#f1f5f9" },
                      })}
                    >
                      {item.label}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
      <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "xs", color: "#64748b" })}>
        <input type="checkbox" checked={manualMode} onChange={(e) => onToggleManual(e.target.checked)} />
        {manualToggleLabel}
      </label>
    </div>
  );
}

/** Modal único para crear y editar cobros (mismo componente, distinto modo según `cobro`). */
export function CobroFormModal({
  open,
  onClose,
  cobro,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** null = modo creación; con datos = modo edición, precargado. */
  cobro: CobroRow | null;
  onSaved: () => void;
}) {
  const { refresh } = useAuth();
  const isEdit = Boolean(cobro);

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const [clientId, setClientId] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientManualMode, setClientManualMode] = useState(false);
  const [clientNameManual, setClientNameManual] = useState("");

  const [serviceId, setServiceId] = useState<string | null>(null);
  const [serviceSearch, setServiceSearch] = useState("");
  const [serviceManualMode, setServiceManualMode] = useState(false);
  const [descriptionManual, setDescriptionManual] = useState("");

  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<string>("efectivo");
  const [status, setStatus] = useState<"pagado" | "pendiente">("pagado");
  const [date, setDate] = useState(todayDateOnly());
  const [notes, setNotes] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Cargar pacientes y servicios del tenant una vez que el modal abre (reusa endpoints existentes
  // del dashboard — no hay que crear ninguno nuevo para esto).
  useEffect(() => {
    if (!open) return;
    setLoadingLists(true);
    void Promise.all([
      fetchWithSessionSync("/api/dashboard/clients", { credentials: "include" }, refresh).then(async (r) =>
        r.ok
          ? ((await r.json()) as { clientId: string | null; displayName: string }[])
              .filter((c) => c.clientId)
              .map((c) => ({ id: c.clientId as string, label: c.displayName }))
          : [],
      ),
      fetchWithSessionSync("/api/dashboard/services", { credentials: "include" }, refresh).then(async (r) =>
        r.ok
          ? ((await r.json()) as { id: string; name: string; price: number | null }[]).map((s) => ({
              id: s.id,
              label: s.name,
              price: s.price,
            }))
          : [],
      ),
    ])
      .then(([c, s]) => {
        setClients(c);
        setServices(s);
      })
      .finally(() => setLoadingLists(false));
  }, [open, refresh]);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setClientId(cobro?.clientId ?? null);
    setClientSearch(cobro?.clientId ? cobro.clientName : "");
    setClientManualMode(cobro ? !cobro.clientId : false);
    setClientNameManual(cobro && !cobro.clientId ? cobro.clientName : "");

    setServiceId(cobro?.serviceId ?? null);
    setServiceSearch(cobro?.serviceId ? cobro.description : "");
    setServiceManualMode(cobro ? !cobro.serviceId : false);
    setDescriptionManual(cobro && !cobro.serviceId ? cobro.description : "");

    setAmount(cobro ? String(cobro.amount) : "");
    setPaymentMethod(cobro?.paymentMethod ?? "efectivo");
    setStatus(cobro?.status ?? "pagado");
    setDate(cobro ? cobro.date.slice(0, 10) : todayDateOnly());
    setNotes(cobro?.notes ?? "");
  }, [open, cobro]);

  function selectService(item: { id: string; label: string }) {
    setServiceId(item.id || null);
    setServiceSearch(item.label);
    if (item.id) {
      const found = services.find((s) => s.id === item.id);
      if (found?.price != null && amount.trim() === "") setAmount(String(found.price));
    }
  }

  async function submit() {
    setErr(null);

    const clientNameValue = clientManualMode ? clientNameManual.trim() : clientSearch.trim();
    if (!clientManualMode && !clientId) {
      setErr("Elegí un paciente de la lista o activá \"Ingresar nombre manualmente\".");
      return;
    }
    if (clientManualMode && !clientNameValue) {
      setErr("Ingresá el nombre del cliente.");
      return;
    }

    const descriptionValue = serviceManualMode ? descriptionManual.trim() : serviceSearch.trim();
    if (!serviceManualMode && !serviceId) {
      setErr("Elegí un servicio de la lista o activá \"Otro concepto\".");
      return;
    }
    if (serviceManualMode && !descriptionValue) {
      setErr("Ingresá una descripción del concepto.");
      return;
    }

    const amountNum = Number(amount.replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setErr("Ingresá un monto válido, mayor a 0.");
      return;
    }

    const body = {
      clientId: clientManualMode ? undefined : clientId ?? undefined,
      clientName: clientManualMode ? clientNameValue : undefined,
      serviceId: serviceManualMode ? undefined : serviceId ?? undefined,
      description: serviceManualMode ? descriptionValue : undefined,
      amount: amountNum,
      paymentMethod,
      status,
      date: new Date(`${date}T12:00:00`).toISOString(),
      notes: notes.trim() || undefined,
    };

    setBusy(true);
    try {
      const r = await fetchWithSessionSync(
        isEdit ? `/api/cobros/${cobro!.id}` : "/api/cobros",
        {
          method: isEdit ? "PUT" : "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        refresh,
      );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setErr(j.error ?? "No se pudo guardar el cobro.");
        return;
      }
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Editar cobro" : "Registrar cobro"} size="md">
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        {err ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}

        <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Cliente</span>
          <EntityPicker
            placeholder="Buscar paciente…"
            items={clients}
            loading={loadingLists}
            selectedId={clientId}
            search={clientSearch}
            onSearchChange={(v) => {
              setClientSearch(v);
              setClientId(null);
            }}
            onSelect={(item) => {
              setClientId(item.id || null);
              setClientSearch(item.label);
            }}
            manualMode={clientManualMode}
            onToggleManual={(v) => {
              setClientManualMode(v);
              if (v) {
                setClientId(null);
                setClientSearch("");
              } else {
                setClientNameManual("");
              }
            }}
            manualValue={clientNameManual}
            onManualChange={setClientNameManual}
            manualPlaceholder="Nombre del cliente"
            manualToggleLabel="Ingresar nombre manualmente"
          />
        </div>

        <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Concepto</span>
          <EntityPicker
            placeholder="Buscar servicio…"
            items={services}
            loading={loadingLists}
            selectedId={serviceId}
            search={serviceSearch}
            onSearchChange={(v) => {
              setServiceSearch(v);
              setServiceId(null);
            }}
            onSelect={selectService}
            manualMode={serviceManualMode}
            onToggleManual={(v) => {
              setServiceManualMode(v);
              if (v) {
                setServiceId(null);
                setServiceSearch("");
              } else {
                setDescriptionManual("");
              }
            }}
            manualValue={descriptionManual}
            onManualChange={setDescriptionManual}
            manualPlaceholder="Ej. Consulta de urgencia"
            manualToggleLabel="Otro concepto"
          />
        </div>

        <div className={css({ display: "grid", gap: "3", gridTemplateColumns: "1fr 1fr" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Monto (ARS)</span>
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Fecha</span>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Método de pago</span>
          <Select value={paymentMethod} onChange={setPaymentMethod} options={PAYMENT_METHOD_OPTIONS} />
        </label>

        <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Estado</span>
          <div className={css({ display: "flex", gap: "2" })}>
            {(["pagado", "pendiente"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={css({
                  flex: 1,
                  px: "3",
                  py: "2",
                  rounded: "md",
                  fontSize: "sm",
                  fontWeight: "semibold",
                  border: "1px solid",
                  borderColor: status === s ? (s === "pagado" ? "#16a34a" : "#d97706") : "#e2e8f0",
                  bg: status === s ? (s === "pagado" ? "#dcfce7" : "#fef3c7") : "white",
                  color: status === s ? (s === "pagado" ? "#14532d" : "#92400e") : "#64748b",
                  cursor: "pointer",
                  transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                  _active: { transform: "scale(0.98)" },
                })}
              >
                {s === "pagado" ? "Pagado" : "Pendiente"}
              </button>
            ))}
          </div>
        </div>

        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Notas (opcional)</span>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </label>

        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end", pt: "2" })}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy}>
            {busy ? "Guardando…" : isEdit ? "Guardar cambios" : "Registrar cobro"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
